/**
 * ReasonDialog (US-044-06 — held earnings review)
 *
 * One reusable dialog for the admin actions the API refuses without a reason:
 * Release / Reverse a held earning and Clear linked account. The submit stays
 * disabled until the trimmed reason is non-empty; a failed call shows the API
 * error message and keeps the dialog open so the admin can retry.
 *
 * Mounted by the parent only while open, so the reason resets on every open.
 * A11y mirrors AgentActionConfirmModal: focus lands in the textarea, Tab is
 * trapped, ESC cancels.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

export interface ReasonDialogProps {
  title: string;
  description: string;
  submitLabel: string;
  /** Resolve on success (parent closes the dialog); throw to show the error. */
  onSubmit: (reason: string) => Promise<void>;
  onCancel: () => void;
  /** Optional richer rendering for a failed submit; default is the error message text. */
  renderError?: (err: unknown) => ReactNode;
}

export function ReasonDialog({
  title,
  description,
  submitLabel,
  onSubmit,
  onCancel,
  renderError,
}: ReasonDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [reason, setReason] = useState('');
  const [isInFlight, setIsInFlight] = useState(false);
  const [error, setError] = useState<ReactNode>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    textareaRef.current?.focus();
    return () => previous?.focus();
  }, []);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (!isInFlight) onCancel();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), textarea:not([disabled])',
          ),
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isInFlight, onCancel]);

  const canSubmit = reason.trim().length > 0 && !isInFlight;

  async function handleSubmit() {
    if (!canSubmit) return;
    setIsInFlight(true);
    setError(null);
    try {
      await onSubmit(reason.trim());
    } catch (err) {
      setError(
        renderError?.(err) ?? (err instanceof Error ? err.message : 'Request failed'),
      );
      setIsInFlight(false);
    }
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 z-[60]"
        aria-hidden="true"
        onClick={() => {
          if (!isInFlight) onCancel();
        }}
      />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="reason-dialog-title"
          aria-describedby="reason-dialog-desc"
          className="w-full max-w-lg bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl flex flex-col gap-4 p-6"
        >
          <h2 id="reason-dialog-title" className="text-sm font-semibold text-zinc-100">
            {title}
          </h2>
          <p id="reason-dialog-desc" className="text-xs text-zinc-400">
            {description}
          </p>

          <div className="flex flex-col gap-1">
            <label htmlFor="reason-dialog-reason" className="text-xs text-zinc-400 font-medium">
              Reason <span className="text-zinc-600 font-normal">(required)</span>
            </label>
            <textarea
              id="reason-dialog-reason"
              ref={textareaRef}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isInFlight}
              rows={3}
              placeholder="Why are you taking this action?"
              className="w-full rounded-lg bg-zinc-950 border border-zinc-700 px-3 py-2 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-violet-500 disabled:opacity-40 resize-y"
            />
          </div>

          {error && (
            <div
              className="text-xs text-red-300 bg-red-950 border border-red-800 rounded-lg px-3 py-2"
              role="alert"
            >
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onCancel}
              disabled={isInFlight}
              aria-label="Cancel — make no change"
              className="min-h-[48px] px-4 py-2 rounded-lg border border-zinc-600 text-zinc-300 text-sm font-medium hover:bg-zinc-800 hover:text-zinc-100 disabled:opacity-40 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={!canSubmit}
              aria-label={submitLabel}
              className="min-h-[48px] px-5 py-2 rounded-lg bg-violet-700 hover:bg-violet-800 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
            >
              {isInFlight ? 'Working…' : submitLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
